/**
 * lds-product-bundle.js
 * Custom element <lds-product-bundle> for the "♥ Pack produit" block.
 *
 * Responsibilities:
 *  1. Pack selection UI (toggle cards).
 *  2. Form intercept: multi-variant packs → POST /cart/add.js with all items.
 *  3. Price sync: ATC button + Essential Sticky Cart always show pack × qty.
 *  4. Variant change: re-render via Section Rendering API (from Dawn or sticky).
 *
 * Dependencies (loaded by theme): subscribe, publish, PUB_SUB_EVENTS, trapFocus
 */
if (!customElements.get('lds-product-bundle')) {
  customElements.define(
    'lds-product-bundle',
    class LdsProductBundle extends HTMLElement {

      /* ── Lifecycle ────────────────────────────────────────────── */

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productUrl = this.dataset.productUrl;
        this.selectedVariantIds = [];
        this._formInterceptors = [];
        this._observers = [];

        this.bindCards();
        this.selectInitialPack();
        this.ensureFormIntercept();
        this.bindStickyCart();
        this.bindQuantity();

        this._variantUnsub = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );
      }

      disconnectedCallback() {
        if (this._variantUnsub) this._variantUnsub();
        this.detachFormIntercept();
        this.unbindStickyCart();
        this.unbindQuantity();
        this._observers.forEach((o) => o.disconnect());
        this._observers = [];
      }

      /* ── Pack selection ───────────────────────────────────────── */

      bindCards() {
        const grid = this.querySelector('.lds-product-bundle__grid');
        if (!grid) return;
        grid.addEventListener('click', (e) => {
          const card = e.target.closest('[data-pack-index]');
          if (card) this.selectPack(card);
        });
      }

      selectInitialPack() {
        const card = this.querySelector('.lds-product-bundle__card.is-selected');
        if (card) {
          this.readVariantIds(card);
          this.syncPrice(card);
        }
      }

      selectPack(card) {
        this.querySelectorAll('.lds-product-bundle__card').forEach((c) => {
          c.classList.remove('is-selected');
          c.setAttribute('aria-pressed', 'false');
        });
        card.classList.add('is-selected');
        card.setAttribute('aria-pressed', 'true');
        this.readVariantIds(card);
        this.syncPrice(card);
      }

      readVariantIds(card) {
        this.selectedVariantIds = (card.dataset.variantIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
      }

      /* ── Price sync ───────────────────────────────────────────── */

      get qtyInput() {
        return document.querySelector('#Quantity-' + this.sectionId);
      }

      get quantity() {
        return Math.max(1, parseInt(this.qtyInput?.value, 10) || 1);
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
        return (cents / 100).toFixed(2).replace('.', ',') + ' \u20AC';
      }

      /**
       * Compute pack price × quantity and push to all price displays.
       * @param {HTMLElement} [card] - defaults to the currently selected card.
       */
      syncPrice(card) {
        if (!card) card = this.querySelector('.lds-product-bundle__card.is-selected');
        if (!card) return;

        const priceRaw = parseInt(card.dataset.packPriceRaw, 10);
        if (!priceRaw && priceRaw !== 0) return;

        const formatted = this.formatMoney(priceRaw * this.quantity);
        this._price = formatted;

        /* Native ATC button price */
        document.querySelectorAll('[data-price-container]').forEach((el) => {
          el.innerHTML =
            '<div class="price"><div class="price__container">' +
            '<div class="price__regular"><span class="price-item price-item--regular">' +
            formatted + '</span></div></div></div>';
        });

        /* Essential Sticky Cart — write pack price to all price slots.
         * When the variant has a compare_at_price, the app swaps layout:
         * "regular-price" becomes the strikethrough, "sale-price" the real one.
         * We force our pack price into both and hide the compare-at element. */
        this._syncStickyPrice();
      }

      /**
       * Push pack price to the sticky-cart.
       * The app uses two price elements:
       *  - "discounted-price": the visible price (sale or normal)
       *  - "regular-price": the strikethrough / compare-at price
       * We write into "discounted-price" and hide "regular-price".
       * Every write is guarded by a content/style check to avoid
       * triggering the MutationObserver in a loop.
       */
      _syncStickyPrice() {
        const portal = document.getElementById('sticky-cart-portal');
        if (!portal || !this._price) return;

        /* Write pack price into the visible price slot */
        portal.querySelectorAll('[data-essential-cart-element="discounted-price"]')
          .forEach((el) => {
            if (el.textContent !== this._price) el.textContent = this._price;
          });

        /* Hide the strikethrough price — no barré on sticky */
        portal.querySelectorAll('[data-essential-cart-element="regular-price"]')
          .forEach((el) => {
            if (el.style.display !== 'none') el.style.display = 'none';
          });
      }

      /* ── Quantity binding ─────────────────────────────────────── */

      bindQuantity() {
        const input = this.qtyInput;
        if (!input) return;

        /* Intercept programmatic value changes (Dawn resets qty after add) */
        const proto = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
        Object.defineProperty(input, 'value', {
          get() { return proto.get.call(this); },
          set: (v) => { proto.set.call(input, v); this.syncPrice(); },
          configurable: true,
        });
        this._qtyDescriptor = proto;
        this._qtyEl = input;

        /* User interactions: change / input events */
        this._onQtyEvent = () => this.syncPrice();
        const container = input.closest('quantity-input') || input.parentElement;
        container.addEventListener('change', this._onQtyEvent);
        container.addEventListener('input', this._onQtyEvent);
        this._qtyContainer = container;
      }

      unbindQuantity() {
        if (this._qtyEl && this._qtyDescriptor) {
          Object.defineProperty(this._qtyEl, 'value', this._qtyDescriptor);
          this._qtyEl = null;
          this._qtyDescriptor = null;
        }
        if (this._qtyContainer) {
          this._qtyContainer.removeEventListener('change', this._onQtyEvent);
          this._qtyContainer.removeEventListener('input', this._onQtyEvent);
          this._qtyContainer = null;
        }
      }

      /* ── Sticky cart (Essential Sticky Cart app) ──────────────── */

      bindStickyCart() {
        /* Document-level capture: the portal is injected async by the app. */
        this._onStickyClick = (e) => {
          const btn = e.target.closest('#sticky-cart-portal [data-essential-cart-element="add-to-cart-button"]');
          if (!btn || this.selectedVariantIds.length <= 1) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          this.addBundleToCart(btn);
        };

        this._onStickyChange = (e) => {
          if (!e.target.closest('#sticky-cart-portal select')) return;
          const id = e.target.value;
          if (id) this.fetchAndReplace(id);
        };

        document.addEventListener('click', this._onStickyClick, { capture: true });
        document.addEventListener('change', this._onStickyChange, { capture: true });

        /**
         * Price guard: observe the PORTAL CONTAINER (not individual price elements).
         * The Essential app is React-based and re-creates DOM nodes on re-render.
         * By observing the container with subtree:true, we catch new nodes too.
         */
        this._stickyPriceGuard = new MutationObserver(() => {
          if (!this._price) return;
          this._syncStickyPrice();
        });

        this._waitForPortal(() => {
          const portal = document.getElementById('sticky-cart-portal');
          if (portal) {
            this._stickyPriceGuard.observe(portal, { childList: true, characterData: true, subtree: true });
          }
        });
      }

      unbindStickyCart() {
        if (this._onStickyClick) document.removeEventListener('click', this._onStickyClick, { capture: true });
        if (this._onStickyChange) document.removeEventListener('change', this._onStickyChange, { capture: true });
        if (this._stickyPriceGuard) this._stickyPriceGuard.disconnect();
        this._onStickyClick = null;
        this._onStickyChange = null;
        this._stickyPriceGuard = null;
      }

      /** Calls fn once #sticky-cart-portal is in the DOM. */
      _waitForPortal(fn) {
        if (document.getElementById('sticky-cart-portal')) { fn(); return; }
        const obs = new MutationObserver(() => {
          if (document.getElementById('sticky-cart-portal')) { obs.disconnect(); fn(); }
        });
        obs.observe(document.body, { childList: true, subtree: true });
        this._observers.push(obs);
      }

      /* ── Form intercept (Dawn product-form) ───────────────────── */

      ensureFormIntercept() {
        if (this.attachFormIntercept()) return;
        const obs = new MutationObserver(() => {
          if (this.attachFormIntercept()) obs.disconnect();
        });
        obs.observe(document.body, { childList: true, subtree: true });
        this._observers.push(obs);
      }

      attachFormIntercept() {
        this.detachFormIntercept();
        const forms = document.querySelectorAll('product-form');
        if (!forms.length) return false;

        forms.forEach((pf) => {
          const form = pf.querySelector('form');
          if (!form) return;
          const handler = (evt) => {
            if (this.selectedVariantIds.length <= 1) return;
            evt.preventDefault();
            evt.stopImmediatePropagation();
            this.addBundleToCart(pf);
          };
          form.addEventListener('submit', handler, { capture: true });
          this._formInterceptors.push({ form, handler });
        });
        return this._formInterceptors.length > 0;
      }

      detachFormIntercept() {
        this._formInterceptors.forEach(({ form, handler }) => {
          form.removeEventListener('submit', handler, { capture: true });
        });
        this._formInterceptors = [];
      }

      /* ── Add to cart ──────────────────────────────────────────── */

      addBundleToCart(originEl) {
        const submitBtn = originEl.querySelector?.('[type="submit"]');
        const spinner = originEl.querySelector?.('.loading__spinner');
        if (submitBtn) { submitBtn.setAttribute('aria-disabled', 'true'); submitBtn.classList.add('loading'); }
        if (spinner) spinner.classList.remove('hidden');

        const qty = this.quantity;
        const items = [...this.selectedVariantIds].reverse().map((id) => ({
          id: parseInt(id, 10),
          quantity: qty,
        }));

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((response) => {
            if (response.status) { this.handleCartError(originEl, response); return; }
            if (!cart) { window.location = window.routes.cart_url; return; }

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cartData: response });

            /* Reset quantity to 1 (mirrors Dawn native behavior) */
            if (this.qtyInput) {
              this.qtyInput.value = '1';
              this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
            }

            /* Patch trapFocus: Dawn may call it with null after multi-item add */
            const origTrap = window.trapFocus;
            window.trapFocus = (el, focusEl) => { if (el) origTrap(el, focusEl); };

            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            try { cart.renderContents(response); }
            catch { window.location.reload(); }
            finally { window.trapFocus = origTrap; }
          })
          .catch(() => { window.location.reload(); })
          .finally(() => {
            if (submitBtn) { submitBtn.removeAttribute('aria-disabled'); submitBtn.classList.remove('loading'); }
            if (spinner) spinner.classList.add('hidden');
          });
      }

      handleCartError(originEl, response) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-bundle',
          errors: response.errors || response.description,
          message: response.message,
        });
        const wrapper = originEl.querySelector?.('.product-form__error-message-wrapper');
        const msg = originEl.querySelector?.('.product-form__error-message');
        if (wrapper && msg) { wrapper.removeAttribute('hidden'); msg.textContent = response.description; }
      }

      /* ── Variant change ───────────────────────────────────────── */

      onVariantChange(event) {
        if (event.data.sectionId !== this.sectionId) return;
        if (event.data.variant) this.fetchAndReplace(event.data.variant.id);
      }

      fetchAndReplace(variantId) {
        fetch(this.productUrl + '?variant=' + variantId + '&section_id=' + this.sectionId)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('lds-product-bundle');
            if (fresh) {
              this.innerHTML = fresh.innerHTML;
              this.bindCards();
              this.selectInitialPack();
              this.ensureFormIntercept();
            } else {
              this.innerHTML = '';
              this.detachFormIntercept();
              this.selectedVariantIds = [];
            }
          });
      }
    }
  );
}
